import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable17 = ({ data }) => {
  const pdfRef = useRef();

  // ---- 🧠 Process Data ----
  const processed = data.map((item) => {
    const start = Number(item.year_2018);
    const end = Number(item.year_2024);
    const growthPct = (((end - start) / start) * 100).toFixed(2);

    return {
      ...item,
      growthPct,
      growthCount: end - start,
    };
  });

  // ---- 📌 Extract Insights ----
  const highestGrowth = processed.reduce((a, b) =>
    Number(a.growthPct) > Number(b.growthPct) ? a : b
  );

  const avgGrowthPct =
    processed.reduce((sum, i) => sum + Number(i.growthPct), 0) /
    processed.length;

  // ---- 📊 Chart Data ----
  const chartData = {
    labels: processed.map((item) => item.education_label),
    datasets: [
      {
        label: "2018",
        data: processed.map((item) => Number(item.year_2018)),
        backgroundColor: "#F6CFFF60",
      },
      {
        label: "2024",
        data: processed.map((item) => Number(item.year_2024)),
        backgroundColor: "#2C92B860",
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Growth by Education Level (2018–2024)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: { title: { text: "Employment", display: true } },
      x: { title: { text: "Education Level", display: true } },
    },
  };

  // ---- 📄 PDF Export ----
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");
      const width = pdf.internal.pageSize.getWidth() - 10;
      const height = (canvas.height * width) / canvas.width;

      // Header branding
      pdf.addImage(utahData, "PNG", 10, 5, 30, 25);
      pdf.setFontSize(22);
      pdf.text("UTahData.org", 52, 20);
      pdf.line(10, 34, 200, 34);

      // Chart/Table
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 5, 38, width, height);

      // Footer
      pdf.line(10, 262, 200, 262);
      pdf.addImage(bodex, "PNG", 15, 266, 35, 13);
      pdf.addImage(kpi, "PNG", 55, 266, 18, 18);

      pdf.setFontSize(10);
      pdf.text(`Exported: ${new Date().toLocaleDateString()}`, 145, 290);
      pdf.text(`Source: ${window.location.href}`, 10, 290);

      pdf.save("EngTable17.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md border border-gray-200"
      >
        <h2 className="text-center text-2xl font-semibold text-gray-900 mb-5">
          Employment Growth by Education Level (2018–2024)
        </h2>

        {/* ---- KPI Cards ---- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-100 p-4 rounded-lg shadow text-center">
            <p className="font-medium text-blue-900">📈 Avg Growth (%)</p>
            <p className="text-xl font-bold text-blue-700">
              {avgGrowthPct.toFixed(2)}%
            </p>
          </div>

          <div className="bg-green-100 p-4 rounded-lg shadow text-center">
            <p className="font-medium text-green-900">🚀 Highest Growth</p>
            <p className="font-bold text-sm">{highestGrowth.education_label}</p>
            <p className="text-lg font-bold text-green-700">
              {highestGrowth.growthPct}%
            </p>
          </div>

          <div className="bg-yellow-100 p-4 rounded-lg shadow text-center">
            <p className="font-medium text-yellow-900">📌 Data Entries</p>
            <p className="text-xl font-bold">{processed.length}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* ---- Table ---- */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Education Level</th>
                    <th>2018</th>
                    <th>2024</th>
                    <th>Growth (#)</th>
                    <th>Growth (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {processed.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="font-medium p-2">{row.education_label}</td>
                      <td>{Number(row.year_2018).toLocaleString("en-US")}</td>
                      <td>{Number(row.year_2024).toLocaleString("en-US")}</td>
                      <td className="font-semibold text-blue-700">
                        {row.growthCount.toLocaleString("en-US")}
                      </td>
                      <td className="font-semibold text-green-600">
                        {row.growthPct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- Chart ---- */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* ---- Button ---- */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable17;
